import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileCheck, Search, Plus, Eye, RefreshCw, X } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const NonSkilledAssessmentPage = () => {
  const [assessments, setAssessments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', assessmentType: 'Initial Assessment',
    assessmentDate: new Date().toISOString().split('T')[0], assessorName: '',
    functionalStatus: '', notes: '', status: 'Draft'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, patientsRes] = await Promise.all([
        api.get('/visit-documents/type/NonSkilled'),
        api.get('/patients')
      ]);
      if (docsRes.data.success) setAssessments(docsRes.data.documents || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName) { toast.error('Please select a patient'); return; }
    try {
      const res = await api.post('/visit-documents', {
        ...formData,
        discipline: 'NonSkilled',
        documentType: formData.assessmentType
      });
      if (res.data.success) {
        toast.success('Assessment created');
        setAssessments(prev => [...prev, res.data.document]);
        setShowModal(false);
        setFormData({
          patientId: '', patientName: '', assessmentType: 'Initial Assessment',
          assessmentDate: new Date().toISOString().split('T')[0], assessorName: '',
          functionalStatus: '', notes: '', status: 'Draft'
        });
      }
    } catch (error) {
      toast.error('Failed to create assessment');
    }
  };

  const filteredAssessments = assessments.filter(a =>
    a.patientName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="non-skilled-assessment-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileCheck className="w-6 h-6 text-amber-600" />
                Non-Skilled Assessment
              </h1>
              <p className="text-gray-500 text-sm mt-1">Document non-skilled patient assessments and functional status</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />New Assessment
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-amber-700">{assessments.length}</div>
            <div className="text-sm text-amber-600">Total Assessments</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{assessments.filter(a => a.status === 'Completed').length}</div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{assessments.filter(a => a.status === 'Draft').length}</div>
            <div className="text-sm text-yellow-600">Pending</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search assessments..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filteredAssessments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No assessments found. Click "New Assessment" to create one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Assessor</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredAssessments.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{a.patientName}</td>
                    <td className="px-6 py-4">{a.documentType || a.assessmentType}</td>
                    <td className="px-6 py-4">{a.visitDate || a.assessmentDate}</td>
                    <td className="px-6 py-4">{a.assessorName || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${a.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Button size="sm" variant="outline"><Eye className="w-4 h-4 mr-1" />View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">New Non-Skilled Assessment</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select value={formData.patientId} onChange={(e) => {
                    const p = patients.find(p => p.id === e.target.value);
                    setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                  }} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Assessment Type</label>
                  <select value={formData.assessmentType} onChange={(e) => setFormData(prev => ({ ...prev, assessmentType: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                    <option>Initial Assessment</option>
                    <option>Reassessment</option>
                    <option>Functional Status Review</option>
                    <option>Care Plan Review</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Assessment Date</label>
                  <Input type="date" value={formData.assessmentDate} onChange={(e) => setFormData(prev => ({ ...prev, assessmentDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Assessor Name</label>
                  <Input value={formData.assessorName} onChange={(e) => setFormData(prev => ({ ...prev, assessorName: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Functional Status</label>
                  <select value={formData.functionalStatus} onChange={(e) => setFormData(prev => ({ ...prev, functionalStatus: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Status</option>
                    <option>Independent</option>
                    <option>Minimal Assistance</option>
                    <option>Moderate Assistance</option>
                    <option>Maximum Assistance</option>
                    <option>Dependent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={3} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleSubmit}>Create</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NonSkilledAssessmentPage;
